'use client'

import { ClearIcon, SearchIcon } from '../../icons'
import React, { KeyboardEvent, SyntheticEvent, useEffect, useRef, useState } from 'react'
import useDebounce from '../../../hooks/useDebounce'
import { Button, ButtonVariants } from '../Button'

export interface SearchProps extends React.HTMLAttributes<HTMLFormElement> {
    placeholder?: string
    searchCallback: (query: string) => void
    delay?: number
    searchTerm?: string | undefined
    variant?: 'light' | 'dark'
    'data-testid-prefix'?: string
}

export const Search = ({
    placeholder = '',
    searchCallback,
    delay = 500,
    searchTerm,
    variant = 'light',
    'data-testid-prefix': testIdPrefix = '',
    ...props
}: SearchProps) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [query, setQuery] = useState<string>(searchTerm || '')
    const debouncedQuery = useDebounce(query, delay)

    useEffect(() => {
        setQuery(searchTerm || '')
    }, [searchTerm])

    useEffect(() => {
        searchCallback(debouncedQuery)
    }, [debouncedQuery])

    const keyPressHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') event.preventDefault()
    }

    const inputHandler = (event: SyntheticEvent<HTMLInputElement>): void => {
        const target = event.target as HTMLInputElement
        setQuery(target.value)
    }

    const clearSearch = (): void => {
        setQuery('')
        if (inputRef?.current) inputRef.current.focus()
    }

    const defaultClasses = 'w-full text-sm text-text-tertiary inline-flex border focus-within:border-accent-600'
    let variantClasses = ''
    switch (variant) {
        case 'light':
            variantClasses = 'bg-shade-0 border-shade-200'
            break
        case 'dark':
            variantClasses = 'bg-shade-opacity-100 border-shade-100'
            break
    }
    const propClasses = props.className ?? ''
    const combinedClasses = [defaultClasses, variantClasses, propClasses].join(' ')

    return (
        <form className={combinedClasses}>
            <input
                ref={inputRef}
                type="text"
                className="search h-12 px-3 inline-flex items-center bg-transparent min-w-0 outline-0 grow"
                placeholder={placeholder}
                onKeyPress={keyPressHandler}
                onChange={inputHandler}
                value={query}
                data-testid={`${testIdPrefix}search-input`}
            />
            {query !== '' && (
                <Button
                    className="shrink-0"
                    variant={ButtonVariants.QUIET}
                    icon={<ClearIcon />}
                    onClick={() => clearSearch()}
                    data-testid={`${testIdPrefix}search-clear-button`}
                />
            )}
            <Button
                className="inline-flex shrink-0"
                variant={ButtonVariants.QUIET}
                icon={<SearchIcon />}
                onClick={() => searchCallback(query)}
                data-testid={`${testIdPrefix}search-button`}
            />
        </form>
    )
}
